import { useRef, useState, type PointerEvent, type ReactNode } from 'react';
import styles from '../Disco.module.css';
import type { BannerNavControl } from '../bannerFormat';

const SWIPE_PX = 32;
const HOLD_MS = 220;
const PREV_ZONE = 0.35;

interface BannerMediaProps {
  variant: 'drawer' | 'inline';
  imageUrl?: string;
  fillColor: string;
  index: number;
  total: number;
  showArrows: boolean;
  tapToNavigate: boolean;
  onPrev: (control: BannerNavControl) => void;
  onNext: (control: BannerNavControl) => void;
  onHoldChange?: (holding: boolean) => void;
  onImageError?: (imageUrl: string) => void;
  children?: ReactNode;
}

function Chevron({ direction }: { direction: 'prev' | 'next' }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d={direction === 'prev' ? 'M12.5 4L7 10l5.5 6' : 'M7.5 4L13 10l-5.5 6'}
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function BannerMedia({
  variant,
  imageUrl,
  fillColor,
  index,
  total,
  showArrows,
  tapToNavigate,
  onPrev,
  onNext,
  onHoldChange,
  onImageError,
  children,
}: BannerMediaProps) {
  const pointerStart = useRef<{ x: number; at: number } | null>(null);
  const [nudges, setNudges] = useState(0);
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = Boolean(imageUrl) && !imageFailed;

  const isFirst = index === 0;
  const isLast = index >= total - 1;

  const goPrev = (control: BannerNavControl) =>
    isFirst ? setNudges((n) => n + 1) : onPrev(control);
  const goNext = (control: BannerNavControl) =>
    isLast ? setNudges((n) => n + 1) : onNext(control);

  const release = () => {
    pointerStart.current = null;
    onHoldChange?.(false);
  };

  const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    pointerStart.current = { x: e.clientX, at: e.timeStamp };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    onHoldChange?.(true);
  };

  const handlePointerUp = (e: PointerEvent<HTMLDivElement>) => {
    const start = pointerStart.current;
    release();
    if (!start) return;
    const dx = e.clientX - start.x;
    if (Math.abs(dx) >= SWIPE_PX) {
      if (dx < 0) goNext('swipe');
      else goPrev('swipe');
      return;
    }
    if (!tapToNavigate || e.timeStamp - start.at > HOLD_MS) return;
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width && (e.clientX - rect.left) / rect.width < PREV_ZONE) {
      goPrev('tap');
    } else {
      goNext('tap');
    }
  };

  const stopPointer = (e: PointerEvent<HTMLButtonElement>) =>
    e.stopPropagation();

  return (
    <div
      data-testid="banner-media"
      className={
        variant === 'drawer' ? styles.bannerMedia : styles.inlineBannerMedia
      }
      style={{ backgroundColor: fillColor }}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={release}
      onPointerLeave={release}
    >
      {showImage && imageUrl && (
        <img
          src={imageUrl}
          alt=""
          draggable={false}
          className={styles.bannerImage}
          onError={() => {
            setImageFailed(true);
            onImageError?.(imageUrl);
          }}
        />
      )}
      {showArrows && !isFirst && (
        <button
          type="button"
          aria-label="Previous offer"
          className={`${styles.bannerArrow} ${styles.bannerArrowPrev}`}
          onPointerDown={stopPointer}
          onPointerUp={stopPointer}
          onClick={() => onPrev('arrow')}
        >
          <Chevron direction="prev" />
        </button>
      )}
      {showArrows && !isLast && (
        <button
          type="button"
          aria-label="Next offer"
          className={`${styles.bannerArrow} ${styles.bannerArrowNext}`}
          onPointerDown={stopPointer}
          onPointerUp={stopPointer}
          onClick={() => onNext('arrow')}
        >
          <Chevron direction="next" />
        </button>
      )}
      {children}
      {nudges > 0 && <div key={nudges} className={styles.bannerNudge} />}
    </div>
  );
}
